import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs, { generateBreadcrumbSchema } from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Piano, Gem, Frame, Shield } from "lucide-react";
import { createServiceSchema, createFAQSchema } from "../seo/schemas";

const SpecialistMovesPage = () => {
  const specialties = [
    { icon: Piano, title: "Pianos", desc: "Grand pianos, uprights, and keyboards safely moved", price: "from £200" },
    { icon: Gem, title: "Antiques", desc: "Careful handling of valuable antique furniture", price: "from £100" },
    { icon: Frame, title: "Artwork", desc: "Professional art handling and custom crating", price: "from £100" },
    { icon: Shield, title: "Fragile Items", desc: "Specialist packing for delicate items", price: "from £75" }
  ];

  const breadcrumbs = [
    { label: "Services", href: "/services" },
    { label: "Specialist Moves", href: "/services/specialist-moves" },
  ];

  const pageFaqs = [
    { q: "How do you move a piano safely?", a: "We use specialist piano moving equipment including skid boards, piano dollies, and heavy-duty straps. Our team is trained specifically in piano handling. All piano moves include a free survey." },
    { q: "Can you move antique furniture?", a: "Yes. We use custom protective wrapping, padding, and crating for antique and high-value furniture. Every piece is individually assessed and packaged." },
    { q: "Is specialist insurance included?", a: "Yes, all specialist moves are fully insured. We can also arrange additional cover for high-value items on request." },
    { q: "Do you provide art crating services?", a: "Yes, we offer custom crating for artwork, mirrors, and glass items. Our team can build bespoke wooden crates to ensure safe transport." },
  ];

  const jsonLd = [
    createServiceSchema("Specialist Removals Cardiff", "Expert piano, antique, artwork and fragile item removals in Cardiff. Custom packaging, trained specialists, fully insured.", "/services/specialist-moves"),
    createFAQSchema(pageFaqs),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Specialist Removals Cardiff | Piano, Antique & Art Movers | Hitech Transit"
        description="Expert handling of pianos, antiques, artwork and fragile items in Cardiff. Custom packaging, trained specialists, fully insured. Get a specialist quote today."
        canonical="/services/specialist-moves"
        jsonLd={jsonLd}
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={breadcrumbs} />
        {/* Hero */}
        <section className="relative py-20 lg:py-32">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/26efd78509a621f4e14a9f19d36c95b199df64a5d0c23447566e6e566450c850.png)`
            }}
          >
            <div className="absolute inset-0 bg-[#0F172A]/70" />
          </div>
          
          <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Specialist Moves
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                White Glove Moving Service
              </h1>
              <p className="text-slate-200 text-lg mb-8">
                For items that require extra care and expertise. Our specialist team handles pianos, 
                antiques, artwork, and other valuable possessions.
              </p>
              <Link to="/book-survey" className="btn-primary inline-flex items-center gap-2" data-testid="specialist-quote-btn">
                Book Free Survey
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Specialties */}
        <section className="section-padding">
          <div className="container-max">
            <div className="text-center mb-12">
              <span className="label-text">Our Specialties</span>
              <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[#0F172A] mt-2">
                Expert Handling for Precious Items
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {specialties.map((item, index) => (
                <div key={item.title || item.name || `item-${index}`} className="card-surface p-8 text-center">
                  <div className="w-14 h-14 bg-[#FF5A1F]/10 mx-auto flex items-center justify-center mb-4">
                    <item.icon className="w-7 h-7 text-[#FF5A1F]" />
                  </div>
                  <h3 className="text-xl font-['Outfit'] font-bold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{item.desc}</p>
                  <div className="text-[#FF5A1F] font-semibold">{item.price}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="section-padding bg-[#F8F9FA]">
          <div className="container-max">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-6">
                Why Choose Our Specialist Service
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Moving valuable or fragile items requires experience, proper equipment, and meticulous 
                attention to detail. Our specialist team has handled thousands of delicate items, 
                from concert grand pianos to priceless antiques.
              </p>
              
              <div className="space-y-4">
                {[
                  "Trained and certified specialists",
                  "Custom packaging and crating",
                  "Climate-controlled vehicles",
                  "Full specialist insurance coverage",
                  "Pre-move condition reports",
                  "Direct supervision throughout"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#FF5A1F] flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-12 bg-[#0F172A] p-6">
                <p className="text-white text-sm">
                  <strong>Important:</strong> All specialist moves require a free survey to assess 
                  your items and provide an accurate quote. This ensures we bring the right equipment 
                  and team for your specific requirements.
                </p>
              </div>
              
              <div className="mt-8">
                <Link to="/book-survey" className="btn-primary inline-flex items-center gap-2" data-testid="specialist-book-survey-btn">
                  Book Free Survey
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-padding bg-white">
          <div className="container-max">
            <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-8">
              Frequently Asked Questions About Specialist Moves
            </h2>
            <div className="max-w-3xl space-y-6">
              {pageFaqs.map((faq, index) => (
                <div key={`faq-${index}`} className="border-b border-slate-200 pb-6">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{faq.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SpecialistMovesPage;
